/**
 * 🎬 HeroDemoVideo — Démo cinematic premium 6 scènes (Iter310)
 * =============================================================
 *
 * Carrousel immersif présentant les 6 flux clés de CoachDIA :
 *   1. Vue d'ensemble plateforme
 *   2. Coach IA Owen (vocal)
 *   3. Parcours certifiant (10 critères)
 *   4. Coaching humain visio
 *   5. Rapport IA post-session
 *   6. Prism / Pulse
 *
 * Stratégie : pas de vidéo .mp4 nécessaire — l'expérience est l'animation
 * orchestrée de mockups SVG/CSS premium qui racontent chaque flux.
 * Si un asset .mp4 existe (`/assets/videos/coachdia-demo.mp4`), il est
 * détecté et utilisé prioritairement (zéro fallback caché : preview = expérience principale).
 *
 * Design system : #FAFAF8 / #1C1147 / #D4AF37 / glassmorphism (cohérent Iter304-309).
 */

import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Play,
  X,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Mic,
  Award,
  Users,
  FileText,
  BarChart3,
  CheckCircle2,
  Eye,
  Brain,
  Zap,
  TrendingUp,
} from 'lucide-react';

export const T = {
  ink: '#1C1147',
  inkSoft: '#3A2E6E',
  ivory: '#FAFAF8',
  border: '#E8E4D9',
  goldDeep: '#B7901F',
  gold: '#D4AF37',
  goldLight: '#F3D060',
  violet: '#7A4FCF',
  violetSoft: '#A78BFA',
  green: '#48825A',
};

/**
 * 🔗 Preview du carrousel home — utilise la même source que la page /demo
 * (`data/demoScenes.config.js`) pour les titres, garantissant zéro divergence.
 *
 * Le mapping est calculé via `useMemo` dans le composant pour éviter un
 * cycle d'import top-level (demoScenes.config importe les SceneFrames d'ici).
 */
import { DEMO_SCENES } from '../data/demoScenes.config';

const _PREVIEW_OVERRIDES = {
  platform:    { kicker: 'Scène 01 · Plateforme',       accent: T.gold,     icon: BarChart3 },
  owen:        { kicker: 'Scène 02 · Coach IA',         accent: T.violet,   icon: Mic },
  structure:   { kicker: 'Scène 03 · Parcours',         accent: T.goldDeep, icon: Award },
  human:       { kicker: 'Scène 04 · Coaching humain',  accent: T.green,    icon: Users },
  report:      { kicker: 'Scène 05 · Rapport IA',       accent: T.violet,   icon: FileText },
  prism:       { kicker: 'Scène 06 · Prism + Pulse',    accent: T.gold,     icon: Eye },
};

const _PREVIEW_ORDER = ['platform', 'owen', 'structure', 'human', 'report', 'prism'];

/**
 * Construit la liste des scènes du carrousel preview en fusionnant la source
 * unique `DEMO_SCENES` avec les overrides visuels (kicker court, accent, icon).
 * Appelé via `useMemo` dans le composant pour briser le cycle d'imports
 * (demoScenes.config importe les SceneFrames d'ici).
 */
function _buildPreviewScenes() {
  const byId = new Map(DEMO_SCENES.map((s) => [s.id, s]));
  return _PREVIEW_ORDER
    .map((id) => {
      const s = byId.get(id);
      if (!s) return null;
      return {
        id: s.id,
        title: s.title,             // ← titre unique partagé avec la démo
        Mockup: s.Frame,
        ..._PREVIEW_OVERRIDES[id],
      };
    })
    .filter(Boolean);
}

const SCENE_DURATION_MS = 10000;

/* ============================================================
   🧩 6 MOCKUPS — chaque scène est un mini-écran premium
   ============================================================ */

export function SceneOverview() {
  return (
    <div className="absolute inset-0 p-5 sm:p-7 flex flex-col gap-3">
      {/* Top bar mock */}
      <div className="flex items-center gap-2 mb-1">
        <div className="w-2 h-2 rounded-full bg-[#FFB4B4]" />
        <div className="w-2 h-2 rounded-full bg-[#FFE0A6]" />
        <div className="w-2 h-2 rounded-full bg-[#B6E3C0]" />
        <div
          className="ml-3 text-[10px] font-mono tracking-wider"
          style={{ color: '#A89F8C' }}
        >
          coachdia.com/dashboard
        </div>
      </div>
      {/* Header */}
      <div className="flex items-baseline gap-3">
        <span className="text-[9px] uppercase tracking-[0.20em] font-semibold" style={{ color: T.goldDeep }}>
          Mon parcours
        </span>
      </div>
      <div
        className="font-serif"
        style={{ color: T.ink, fontSize: '1.05rem', fontWeight: 600, lineHeight: 1.15 }}
      >
        Les 5 piliers du leadership moderne
      </div>
      {/* 5 piliers */}
      <div className="grid grid-cols-5 gap-1.5 mt-1">
        {[100, 75, 60, 30, 0].map((p, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 + i * 0.08 }}
            className="rounded-lg p-1.5 flex flex-col gap-1 relative overflow-hidden"
            style={{
              background: '#FFFFFF',
              border: `1px solid ${T.border}`,
              boxShadow: '0 4px 10px -6px rgba(28,17,71,0.18)',
            }}
          >
            <div
              className="text-[8px] font-bold"
              style={{ color: p === 100 ? T.green : p > 0 ? T.goldDeep : '#A89F8C' }}
            >
              0{i + 1}
            </div>
            <div className="h-0.5 rounded-full overflow-hidden" style={{ background: 'rgba(28,17,71,0.06)' }}>
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${p}%` }}
                transition={{ duration: 0.9, delay: 0.3 + i * 0.08 }}
                className="h-full"
                style={{
                  background:
                    p === 100
                      ? `linear-gradient(90deg, ${T.green}, #6BA77E)`
                      : `linear-gradient(90deg, ${T.goldDeep}, ${T.goldLight})`,
                }}
              />
            </div>
          </motion.div>
        ))}
      </div>

      {/* 📚 Liste 6 modules — apparition en cascade (remplit le vide central) */}
      <div className="mt-2 space-y-1">
        {[
          { num: '01', name: 'Cadre de mission', pct: 100, status: 'validé' },
          { num: '02', name: 'Délégation efficace', pct: 86, status: 'en cours' },
          { num: '03', name: 'Communication d\'impact', pct: 64, status: 'en cours' },
          { num: '04', name: 'Décision sous incertitude', pct: 41, status: 'en cours' },
          { num: '05', name: 'Posture managériale', pct: 22, status: 'à venir' },
          { num: '06', name: 'Coaching d\'équipe', pct: 0, status: 'à venir' },
        ].map((m, i) => (
          <motion.div
            key={m.num}
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.75 + i * 0.06, duration: 0.4 }}
            className="flex items-center gap-2.5 px-2 py-1 rounded-md"
            style={{
              background: m.pct === 100 ? 'rgba(72,130,90,0.06)' : m.pct > 0 ? 'rgba(212,175,55,0.05)' : 'transparent',
            }}
          >
            <span
              className="text-[8.5px] font-bold tabular-nums"
              style={{ color: m.pct === 100 ? T.green : m.pct > 0 ? T.goldDeep : '#A89F8C', minWidth: 14 }}
            >
              {m.num}
            </span>
            <span className="text-[10.5px] font-medium flex-1 truncate" style={{ color: T.ink }}>
              {m.name}
            </span>
            <div className="w-12 h-0.5 rounded-full overflow-hidden flex-shrink-0" style={{ background: 'rgba(28,17,71,0.07)' }}>
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${m.pct}%` }}
                transition={{ duration: 0.7, delay: 0.85 + i * 0.06 }}
                className="h-full"
                style={{
                  background: m.pct === 100
                    ? `linear-gradient(90deg, ${T.green}, #6BA77E)`
                    : m.pct > 0
                      ? `linear-gradient(90deg, ${T.goldDeep}, ${T.goldLight})`
                      : '#E0DAC8',
                }}
              />
            </div>
            <span
              className="text-[8.5px] font-semibold tabular-nums"
              style={{ color: m.pct === 100 ? T.green : m.pct > 0 ? T.goldDeep : '#A89F8C', minWidth: 26, textAlign: 'right' }}
            >
              {m.pct}%
            </span>
          </motion.div>
        ))}
      </div>

      {/* Owen cockpit mini */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.3 }}
        className="mt-auto rounded-xl p-3 flex items-center gap-3"
        style={{
          background: 'linear-gradient(135deg, #FFFCEC 0%, #FFFFFF 100%)',
          border: `1px solid rgba(212,175,55,0.35)`,
        }}
      >
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{
            background: `linear-gradient(135deg, ${T.violet}, ${T.violetSoft})`,
            boxShadow: '0 6px 14px -6px rgba(122,79,207,0.55)',
          }}
        >
          <Brain className="w-4 h-4 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em]" style={{ color: T.violet }}>
            Owen · Centre de pilotage
          </div>
          <div className="text-[12px] font-medium truncate" style={{ color: T.ink }}>
            Continuer mon évolution →
          </div>
        </div>
        <Sparkles className="w-4 h-4" style={{ color: T.goldDeep }} />
      </motion.div>
    </div>
  );
}

export function SceneOwen() {
  const bars = [16, 28, 22, 36, 24, 40, 30, 22, 34, 20, 28, 18, 32, 24];
  return (
    <div className="absolute inset-0 p-5 sm:p-7 flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <span className="text-[9px] uppercase tracking-[0.20em] font-semibold" style={{ color: T.violet }}>
          Coach IA · Owen
        </span>
        <span
          className="ml-auto inline-flex items-center gap-1 text-[9px] font-bold px-1.5 py-0.5 rounded-full"
          style={{ background: 'rgba(232,90,90,0.12)', color: '#C44B6F' }}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-[#C44B6F] animate-pulse" /> LIVE
        </span>
      </div>
      <div className="flex items-center gap-3">
        <motion.div
          animate={{ scale: [1, 1.08, 1] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
          className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
          style={{
            background: `linear-gradient(135deg, ${T.violet}, ${T.violetSoft})`,
            boxShadow: `0 0 0 6px ${T.violet}22, 0 12px 24px -10px ${T.violet}88`,
          }}
        >
          <Mic className="w-5 h-5 text-white" />
        </motion.div>
        <div className="flex-1 min-w-0">
          <div className="text-[12px] font-semibold" style={{ color: T.ink }}>
            Owen écoute…
          </div>
          <div className="text-[10px]" style={{ color: T.inkSoft }}>
            00:42 — Module · Délégation efficace
          </div>
        </div>
      </div>
      {/* Waveform */}
      <div className="flex items-end gap-[3px] h-10">
        {bars.map((h, i) => (
          <motion.div
            key={i}
            animate={{ height: [`${h}%`, `${Math.min(100, h + 18)}%`, `${h}%`] }}
            transition={{ duration: 1.1, delay: i * 0.05, repeat: Infinity, ease: 'easeInOut' }}
            className="flex-1 rounded-full"
            style={{
              background: `linear-gradient(180deg, ${T.violet}, ${T.violetSoft})`,
              minHeight: 4,
            }}
          />
        ))}
      </div>
      {/* Conversation simulée — bulles typing en cascade */}
      <motion.div
        initial={{ opacity: 0, x: -8 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.3 }}
        className="rounded-xl p-2.5 mt-1"
        style={{
          background: '#FFFFFF',
          border: `1px solid ${T.border}`,
          boxShadow: '0 4px 12px -8px rgba(28,17,71,0.18)',
        }}
      >
        <div className="text-[9.5px] font-semibold mb-0.5" style={{ color: T.violet }}>
          Owen
        </div>
        <div className="text-[11.5px] leading-snug" style={{ color: T.ink }}>
          « Vous avez délégué la décision sans cadrer le critère de réussite. Quelle preuve attendrez-vous ? »
        </div>
      </motion.div>

      {/* Réponse manager (typing dots puis texte) */}
      <motion.div
        initial={{ opacity: 0, x: 8 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 1.4 }}
        className="rounded-xl p-2.5 self-end max-w-[88%]"
        style={{
          background: 'linear-gradient(135deg, #FFFCEC 0%, #FFFFFF 100%)',
          border: `1px solid rgba(212,175,55,0.35)`,
          boxShadow: '0 4px 12px -8px rgba(212,175,55,0.30)',
        }}
      >
        <div className="text-[9.5px] font-semibold mb-0.5 text-right" style={{ color: T.goldDeep }}>
          Vous
        </div>
        <div className="text-[11.5px] leading-snug text-right" style={{ color: T.ink }}>
          « Une note écrite, partagée à l'équipe sous 7 jours, avec mesure de l'impact. »
        </div>
      </motion.div>

      {/* 2e relance Owen */}
      <motion.div
        initial={{ opacity: 0, x: -8 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 2.5 }}
        className="rounded-xl p-2 mt-0.5"
        style={{
          background: '#FFFFFF',
          border: `1px solid ${T.border}`,
        }}
      >
        <div className="flex items-center gap-1.5 mb-0.5">
          <span className="text-[9.5px] font-semibold" style={{ color: T.violet }}>Owen</span>
          <motion.span
            className="flex gap-0.5"
            animate={{ opacity: [0.4, 1, 0.4] }}
            transition={{ duration: 1.2, repeat: Infinity }}
          >
            <span className="w-1 h-1 rounded-full" style={{ background: T.violet }} />
            <span className="w-1 h-1 rounded-full" style={{ background: T.violet }} />
            <span className="w-1 h-1 rounded-full" style={{ background: T.violet }} />
          </motion.span>
        </div>
        <div className="text-[10.5px] leading-snug italic" style={{ color: T.inkSoft }}>
          « Précisez la mesure d'impact. Owen prépare la suite. »
        </div>
      </motion.div>
      <div className="flex items-center gap-2 mt-auto">
        <span
          className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full"
          style={{
            background: 'rgba(212,175,55,0.12)',
            color: T.goldDeep,
            border: `1px solid rgba(212,175,55,0.35)`,
          }}
        >
          <Zap className="w-3 h-3" /> Challenge en temps réel
        </span>
      </div>
    </div>
  );
}

export function SceneCertifiant() {
  const dots = Array.from({ length: 10 });
  const validated = 7;
  return (
    <div className="absolute inset-0 p-5 sm:p-7 flex flex-col gap-3">
      <span className="text-[9px] uppercase tracking-[0.20em] font-semibold" style={{ color: T.goldDeep }}>
        Module 03 · Posture & Fondamentaux
      </span>
      <div
        className="font-serif"
        style={{ color: T.ink, fontSize: '1rem', fontWeight: 600, lineHeight: 1.2 }}
      >
        Clarifier sa mission et ses priorités
      </div>
      <div
        className="rounded-xl p-3"
        style={{
          background: 'linear-gradient(180deg, #FBF8EE 0%, #FFFFFF 100%)',
          border: `1px solid ${T.border}`,
        }}
      >
        <div className="flex items-center justify-between mb-2">
          <span className="text-[9px] uppercase tracking-[0.14em] font-semibold" style={{ color: T.inkSoft }}>
            Preuves à démontrer
          </span>
          <span className="text-[10px] font-bold tabular-nums" style={{ color: T.goldDeep }}>
            {validated}/10 critères
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          {dots.map((_, i) => {
            const filled = i < validated;
            return (
              <motion.span
                key={i}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.15 + i * 0.06, type: 'spring', stiffness: 240 }}
                className="rounded-full"
                style={{
                  width: 10,
                  height: 10,
                  background: filled ? T.goldDeep : 'rgba(28,17,71,0.10)',
                  boxShadow: filled ? `0 0 8px ${T.gold}` : 'none',
                }}
              />
            );
          })}
        </div>
        <div className="flex items-center gap-2 mt-3">
          <div className="flex gap-1">
            <span
              className="block rounded-[3px]"
              style={{ width: 18, height: 4, background: `linear-gradient(90deg, ${T.goldDeep}, ${T.gold})` }}
            />
            <span
              className="block rounded-[3px]"
              style={{ width: 18, height: 4, background: 'rgba(28,17,71,0.10)' }}
            />
          </div>
          <span className="text-[10px] font-medium" style={{ color: T.inkSoft }}>
            1/2 objectifs
          </span>
          <span className="ml-auto text-[10px] font-bold tabular-nums" style={{ color: T.ink }}>
            70%
          </span>
        </div>
      </div>
      {/* Certificat preview */}
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.9 }}
        className="mt-auto rounded-xl p-3 flex items-center gap-3 relative overflow-hidden"
        style={{
          background: `linear-gradient(135deg, #FFFCEC 0%, #FFF4D2 100%)`,
          border: `1.5px solid rgba(212,175,55,0.45)`,
        }}
      >
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{
            background: `linear-gradient(135deg, ${T.goldDeep} 0%, ${T.gold} 50%, ${T.goldLight} 100%)`,
            boxShadow: '0 8px 18px -8px rgba(212,175,55,0.55)',
          }}
        >
          <Award className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em]" style={{ color: T.goldDeep }}>
            Certificat opposable
          </div>
          <div className="text-[12px] font-medium" style={{ color: T.ink }}>
            Délivré à la validation finale
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export function SceneHumain() {
  return (
    <div className="absolute inset-0 p-5 sm:p-7 flex flex-col gap-3">
      <span className="text-[9px] uppercase tracking-[0.20em] font-semibold" style={{ color: T.green }}>
        Coaching humain · Visio
      </span>
      <div className="grid grid-cols-2 gap-2 flex-1">
        {/* Coach */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.15 }}
          className="rounded-xl relative overflow-hidden flex items-end p-2"
          style={{
            background: `linear-gradient(135deg, #2E1F5C 0%, #4A3690 100%)`,
            minHeight: 90,
          }}
        >
          <span
            className="text-[9px] font-semibold px-1.5 py-0.5 rounded text-white"
            style={{ background: 'rgba(0,0,0,0.45)' }}
          >
            Sophie · Coach
          </span>
          {/* Silhouette */}
          <svg
            className="absolute top-2 left-1/2 -translate-x-1/2 opacity-90"
            width="42"
            height="48"
            viewBox="0 0 42 48"
            fill="none"
          >
            <circle cx="21" cy="14" r="9" fill="#F3D060" />
            <path d="M4 48c0-10 7.6-18 17-18s17 8 17 18" fill="#F3D060" />
          </svg>
          <span
            className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full"
            style={{ background: '#48825A', boxShadow: '0 0 6px #48825A' }}
          />
        </motion.div>
        {/* Manager */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3 }}
          className="rounded-xl relative overflow-hidden flex items-end p-2"
          style={{
            background: `linear-gradient(135deg, #3A2680 0%, #6D28D9 100%)`,
            minHeight: 90,
          }}
        >
          <span
            className="text-[9px] font-semibold px-1.5 py-0.5 rounded text-white"
            style={{ background: 'rgba(0,0,0,0.45)' }}
          >
            Vous
          </span>
          <svg
            className="absolute top-2 left-1/2 -translate-x-1/2 opacity-90"
            width="42"
            height="48"
            viewBox="0 0 42 48"
            fill="none"
          >
            <circle cx="21" cy="14" r="9" fill="#A78BFA" />
            <path d="M4 48c0-10 7.6-18 17-18s17 8 17 18" fill="#A78BFA" />
          </svg>
        </motion.div>
      </div>
      {/* Controls */}
      <div className="flex items-center justify-center gap-2 mt-1">
        {[Mic, Eye, X].map((Icon, i) => (
          <div
            key={i}
            className="w-8 h-8 rounded-full flex items-center justify-center"
            style={{
              background: i === 2 ? '#C44B6F' : '#FFFFFF',
              border: i === 2 ? 'none' : `1px solid ${T.border}`,
              boxShadow: '0 4px 10px -6px rgba(28,17,71,0.18)',
            }}
          >
            <Icon className="w-3.5 h-3.5" style={{ color: i === 2 ? '#FFFFFF' : T.ink }} />
          </div>
        ))}
      </div>
      <div
        className="rounded-lg px-3 py-2 mt-1"
        style={{
          background: 'rgba(72,130,90,0.08)',
          border: '1px solid rgba(72,130,90,0.30)',
        }}
      >
        <div className="text-[10px] font-semibold" style={{ color: T.green }}>
          Coachs certifiés · Sessions enregistrées
        </div>
      </div>
    </div>
  );
}

// Hook typing effet — affiche progressivement le texte caractère par caractère
function useTyping(text, speed = 22, startDelay = 0) {
  const [out, setOut] = React.useState('');
  React.useEffect(() => {
    setOut('');
    let i = 0;
    let raf;
    const start = performance.now() + startDelay;
    const tick = (now) => {
      if (now < start) {
        raf = requestAnimationFrame(tick);
        return;
      }
      const elapsed = now - start;
      const target = Math.min(text.length, Math.floor(elapsed / speed));
      if (target !== i) {
        i = target;
        setOut(text.slice(0, i));
      }
      if (i < text.length) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [text, speed, startDelay]);
  return out;
}

function TypingItem({ text, color, delay }) {
  const out = useTyping(text, 14, delay);
  return (
    <div className="flex items-start gap-1" style={{ minHeight: 22 }}>
      <span
        className="rounded-full mt-1 flex-shrink-0"
        style={{ width: 4, height: 4, background: color }}
      />
      <span className="text-[9px] leading-snug" style={{ color: T.ink }}>
        {out}
        {out.length < text.length && (
          <span
            className="inline-block w-[1px] ml-[1px]"
            style={{
              background: color,
              height: 9,
              verticalAlign: '-1px',
              animation: 'blink 0.9s steps(1) infinite',
            }}
          />
        )}
      </span>
    </div>
  );
}

function ReportColumn({ tag, color, items, baseDelay }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: baseDelay, duration: 0.4 }}
      className="rounded-lg p-1.5 flex flex-col gap-1"
      style={{
        background: '#FFFFFF',
        border: `1px solid ${color}33`,
        boxShadow: `0 4px 10px -8px ${color}77`,
      }}
    >
      <div
        className="text-[8px] uppercase tracking-[0.16em] font-bold"
        style={{ color }}
      >
        {tag}
      </div>
      {items.map((it, i) => (
        <TypingItem
          key={i}
          text={it}
          color={color}
          delay={baseDelay * 1000 + 250 + i * 320}
        />
      ))}
    </motion.div>
  );
}

export function SceneRapport() {
  const lines = [85, 92, 78, 88];
  const dimensions = [
    { label: 'Clarté du cadre', score: lines[0] },
    { label: 'Écoute active', score: lines[1] },
    { label: 'Décision factuelle', score: lines[2] },
    { label: 'Suivi des engagements', score: lines[3] },
  ];

  return (
    <div className="absolute inset-0 p-4 sm:p-5 flex flex-col gap-2">
      <style>{`@keyframes blink { 50% { opacity: 0; } }`}</style>
      <div className="flex items-center justify-between">
        <span
          className="text-[9px] uppercase tracking-[0.20em] font-semibold"
          style={{ color: T.violet }}
        >
          Rapport IA · Post-session
        </span>
        <FileText className="w-3.5 h-3.5" style={{ color: T.violet }} />
      </div>
      <div
        className="font-serif"
        style={{ color: T.ink, fontSize: '0.92rem', fontWeight: 600, lineHeight: 1.18 }}
      >
        Synthèse confidentielle · Session coach humain
      </div>

      {/* 4 dimensions compactes */}
      <div className="space-y-1">
        {dimensions.map((d, i) => (
          <motion.div
            key={d.label}
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.15 + i * 0.08 }}
            className="flex items-center gap-2"
          >
            <span
              className="text-[9.5px] font-medium w-[42%] truncate"
              style={{ color: T.ink }}
            >
              {d.label}
            </span>
            <div
              className="flex-1 h-1 rounded-full overflow-hidden"
              style={{ background: 'rgba(28,17,71,0.06)' }}
            >
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${d.score}%` }}
                transition={{ duration: 0.9, delay: 0.3 + i * 0.08 }}
                className="h-full rounded-full"
                style={{
                  background: `linear-gradient(90deg, ${T.violet}, ${T.violetSoft})`,
                }}
              />
            </div>
            <span
              className="text-[9px] font-bold tabular-nums w-7 text-right"
              style={{ color: T.violet }}
            >
              {d.score}
            </span>
          </motion.div>
        ))}
      </div>

      {/* Grille 3 colonnes — Points forts / Axes / Recommandations (typing) */}
      <div className="grid grid-cols-3 gap-1.5 mt-1">
        <ReportColumn
          tag="Forts"
          color={T.green}
          baseDelay={0.7}
          items={[
            'Cadre énoncé clairement.',
            'Preuves alignées sur les faits.',
          ]}
        />
        <ReportColumn
          tag="Axes"
          color="#E0A641"
          baseDelay={0.95}
          items={[
            'Affiner le cadrage initial.',
            'Mieux séquencer les preuves.',
          ]}
        />
        <ReportColumn
          tag="Recos"
          color={T.violet}
          baseDelay={1.20}
          items={[
            'Module 03 · Posture en suivant.',
            'Valider 1 preuve écrite par semaine.',
          ]}
        />
      </div>

      {/* Plan d'action 30/60/90 — timeline animée (densifie le centre du rapport) */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.55 }}
        className="rounded-lg p-2"
        style={{
          background: '#FFFFFF',
          border: `1px solid ${T.border}`,
        }}
      >
        <div
          className="text-[8px] uppercase tracking-[0.18em] font-bold mb-1.5"
          style={{ color: T.violet }}
        >
          Plan d'action · 30 / 60 / 90 jours
        </div>
        <div className="grid grid-cols-3 gap-1.5">
          {[
            {
              tag: '30j',
              label: 'Cadrer 3 décisions',
              progress: 100,
              color: T.green,
            },
            {
              tag: '60j',
              label: 'Documenter 5 preuves',
              progress: 55,
              color: T.violet,
            },
            {
              tag: '90j',
              label: 'Module 03 certifié',
              progress: 12,
              color: T.goldDeep,
            },
          ].map((step, i) => (
            <motion.div
              key={step.tag}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 1.7 + i * 0.15, type: 'spring', stiffness: 260 }}
              className="rounded-md p-1.5 relative"
              style={{
                background: `linear-gradient(135deg, ${step.color}10 0%, transparent 80%)`,
                border: `1px solid ${step.color}33`,
              }}
            >
              <div className="flex items-center gap-1 mb-0.5">
                <span
                  className="rounded-full"
                  style={{
                    width: 4,
                    height: 4,
                    background: step.color,
                    boxShadow: `0 0 6px ${step.color}`,
                  }}
                />
                <span
                  className="text-[8.5px] uppercase tracking-[0.16em] font-bold"
                  style={{ color: step.color }}
                >
                  {step.tag}
                </span>
              </div>
              <div
                className="text-[9px] font-medium leading-tight mb-1"
                style={{ color: T.ink }}
              >
                {step.label}
              </div>
              <div
                className="h-0.5 rounded-full overflow-hidden"
                style={{ background: 'rgba(28,17,71,0.06)' }}
              >
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${step.progress}%` }}
                  transition={{ duration: 0.9, delay: 2.0 + i * 0.15 }}
                  className="h-full"
                  style={{ background: step.color }}
                />
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Footer feedback IA */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.9 }}
        className="rounded-lg p-2"
        style={{
          background: 'linear-gradient(135deg, #F4EFFF 0%, #FFFFFF 100%)',
          border: `1px solid rgba(122,79,207,0.30)`,
        }}
      >
        <div className="flex items-center gap-1.5 mb-0.5">
          <Sparkles className="w-3 h-3" style={{ color: T.violet }} />
          <span
            className="text-[9px] font-semibold uppercase tracking-[0.14em]"
            style={{ color: T.violet }}
          >
            Synthèse IA · Confidentiel
          </span>
        </div>
        <div className="text-[10px] leading-snug" style={{ color: T.ink }}>
          Posture solide, à consolider sur le cadrage initial.
        </div>
      </motion.div>
    </div>
  );
}

export function ScenePrism() {
  // Radar 5 axes — chaque sommet est un % de compétence
  const axes = [
    { label: 'Clarté',   value: 86, angle: -90 },
    { label: 'Décision', value: 74, angle: -18 },
    { label: 'Écoute',   value: 92, angle:  54 },
    { label: 'Posture',  value: 80, angle: 126 },
    { label: 'Vision',   value: 78, angle: 198 },
  ];
  const cx = 50, cy = 50;
  const radius = (v) => (v / 100) * 36;
  const point = (angleDeg, v) => {
    const rad = (angleDeg * Math.PI) / 180;
    return [cx + radius(v) * Math.cos(rad), cy + radius(v) * Math.sin(rad)];
  };
  const polygonPoints = axes.map((a) => point(a.angle, a.value).join(',')).join(' ');

  return (
    <div className="absolute inset-0 p-4 sm:p-5 flex flex-col gap-2.5">
      {/* Header */}
      <div className="flex items-baseline justify-between">
        <span className="text-[9px] uppercase tracking-[0.20em] font-semibold" style={{ color: T.goldDeep }}>
          Prism · Profil de leadership
        </span>
        <span className="text-[10px] font-semibold" style={{ color: T.goldDeep }}>
          Score global · 82
        </span>
      </div>
      <div className="font-serif" style={{ color: T.ink, fontSize: '0.95rem', fontWeight: 600, lineHeight: 1.15 }}>
        Architecte stratégique
      </div>

      {/* Radar + Forces / Axes */}
      <div className="grid grid-cols-[140px_1fr] sm:grid-cols-[160px_1fr] gap-3 items-center">
        {/* Radar SVG animé */}
        <div className="flex flex-col items-center">
          <svg viewBox="0 0 100 100" width="100%" height="auto" style={{ maxWidth: 150 }}>
            {/* Grilles concentriques */}
            {[0.3, 0.6, 1].map((scale, i) => (
              <polygon
                key={i}
                points={axes.map((a) => point(a.angle, 100 * scale).join(',')).join(' ')}
                fill="none"
                stroke={T.gold}
                strokeWidth="0.4"
                opacity={0.18 + i * 0.08}
              />
            ))}
            {/* Axes radiaux */}
            {axes.map((a, i) => {
              const [x, y] = point(a.angle, 100);
              return <line key={i} x1={cx} y1={cy} x2={x} y2={y} stroke={T.gold} strokeWidth="0.3" opacity="0.25" />;
            })}
            {/* Polygone score */}
            <motion.polygon
              initial={{ opacity: 0, scale: 0.4 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.0, ease: [0.22, 1, 0.36, 1] }}
              style={{ transformOrigin: '50px 50px' }}
              points={polygonPoints}
              fill={`${T.gold}44`}
              stroke={T.goldDeep}
              strokeWidth="1.4"
            />
            {/* Points sommets */}
            {axes.map((a, i) => {
              const [x, y] = point(a.angle, a.value);
              return (
                <motion.circle
                  key={i}
                  cx={x} cy={y} r="1.6"
                  fill={T.goldDeep}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.6 + i * 0.08 }}
                />
              );
            })}
          </svg>
          {/* Labels axes (compact) */}
          <div className="grid grid-cols-5 gap-0.5 mt-1 w-full">
            {axes.map((a, i) => (
              <span key={i} className="text-[7px] text-center font-semibold" style={{ color: T.inkSoft }}>
                {a.label}
              </span>
            ))}
          </div>
        </div>

        {/* Forces / Axes de progression */}
        <div className="flex flex-col gap-2">
          <div>
            <div className="text-[8.5px] uppercase tracking-[0.16em] font-semibold mb-1" style={{ color: T.green }}>
              Forces
            </div>
            <div className="flex flex-wrap gap-1">
              {['Écoute active', 'Cadrage clair'].map((t, i) => (
                <motion.span
                  key={t}
                  initial={{ opacity: 0, x: -6 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 + i * 0.1 }}
                  className="text-[9px] px-2 py-0.5 rounded-full font-semibold"
                  style={{ background: 'rgba(72,130,90,0.14)', color: T.green, border: '1px solid rgba(72,130,90,0.32)' }}
                >
                  {t}
                </motion.span>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[8.5px] uppercase tracking-[0.16em] font-semibold mb-1" style={{ color: T.violet }}>
              Axes de progression
            </div>
            <div className="flex flex-wrap gap-1">
              {['Décision sous incertitude', 'Tempo'].map((t, i) => (
                <motion.span
                  key={t}
                  initial={{ opacity: 0, x: -6 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.65 + i * 0.1 }}
                  className="text-[9px] px-2 py-0.5 rounded-full font-semibold"
                  style={{ background: 'rgba(122,79,207,0.14)', color: T.violet, border: '1px solid rgba(122,79,207,0.32)' }}
                >
                  {t}
                </motion.span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Plan 30 / 60 / 90 jours */}
      <div className="grid grid-cols-3 gap-1.5">
        {[
          { label: '30 j', text: 'Cadrer décisions critiques', delay: 0.85 },
          { label: '60 j', text: 'Test posture sous tension', delay: 1.05 },
          { label: '90 j', text: 'Restitution équipe', delay: 1.25 },
        ].map((s) => (
          <motion.div
            key={s.label}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: s.delay }}
            className="rounded-lg px-2 py-1.5"
            style={{
              background: '#FFFFFF',
              border: `1px solid ${T.border}`,
              boxShadow: '0 4px 8px -6px rgba(28,17,71,0.16)',
            }}
          >
            <div className="text-[8px] font-bold uppercase tracking-[0.18em]" style={{ color: T.goldDeep }}>
              {s.label}
            </div>
            <div className="text-[9.5px] leading-tight font-semibold" style={{ color: T.ink }}>
              {s.text}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Recommandation Owen + sparkline */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="rounded-lg px-2.5 py-1.5 flex items-center justify-between gap-2"
        style={{
          background: 'linear-gradient(135deg, #FFFCEC 0%, #FFF4D2 100%)',
          border: `1px solid rgba(212,175,55,0.35)`,
        }}
      >
        <div className="flex items-center gap-1.5 flex-1 min-w-0">
          <Sparkles className="w-3 h-3 flex-shrink-0" style={{ color: T.goldDeep }} />
          <span className="text-[9.5px] font-semibold truncate" style={{ color: T.ink }}>
            Owen recommande › Module 07 — Décision sous incertitude
          </span>
        </div>
        {/* Mini sparkline +12 pts */}
        <div className="flex items-center gap-1 flex-shrink-0">
          <svg width="40" height="14" viewBox="0 0 40 14">
            <motion.path
              d="M2 11 L10 9 L18 7 L26 5 L34 3"
              stroke={T.green}
              strokeWidth="1.5"
              fill="none"
              strokeLinecap="round"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 0.9, delay: 1.6 }}
            />
            <motion.circle
              cx="34" cy="3" r="1.8" fill={T.green}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 2.4 }}
            />
          </svg>
          <span className="text-[9px] font-bold" style={{ color: T.green }}>+12 pts</span>
        </div>
      </motion.div>
    </div>
  );
}

/* ============================================================
   🎬 SCENE FRAME — affiche une scène + caption (caption optionnel)
   ============================================================ */
export const SceneFrame = ({ scene, big = false, showCaption = true }) => {
  const Mockup = scene.Mockup;
  const Icon = scene.icon;
  return (
    <div className="absolute inset-0">
      {/* Inner mockup frame (white sheet) */}
      <div
        className="absolute inset-0 rounded-[18px] overflow-hidden"
        style={{
          background: 'linear-gradient(180deg, #FFFFFF 0%, #FAFAF8 100%)',
          border: `1px solid ${T.border}`,
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.6), 0 1px 0 rgba(28,17,71,0.04)',
        }}
      >
        <Mockup />
      </div>
      {/* Caption overlay bottom (utilisé par le modal uniquement) */}
      {showCaption && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className={`absolute left-3 right-3 ${big ? 'bottom-5' : 'bottom-3'} rounded-xl px-3 py-2 flex items-center gap-2`}
          style={{
            background: 'rgba(28,17,71,0.88)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            border: `1px solid rgba(212,175,55,0.30)`,
          }}
        >
          <span
            className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ background: scene.accent + '33' }}
          >
            <Icon className="w-3.5 h-3.5" style={{ color: scene.accent }} />
          </span>
          <div className="flex-1 min-w-0">
            <div
              className="text-[9px] uppercase tracking-[0.18em] font-semibold"
              style={{ color: scene.accent }}
            >
              {scene.kicker}
            </div>
            <div
              className={`${big ? 'text-[14px]' : 'text-[12px]'} font-medium leading-tight text-white truncate`}
            >
              {scene.title}
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

/* ============================================================
   📰 CAPTION STRIP — bandeau lumineux SOUS la zone visuelle (preview)
   ============================================================ */
const CaptionStrip = ({ scene }) => {
  const Icon = scene.icon;
  return (
    <div
      className="px-5 py-3.5"
      style={{
        background: 'linear-gradient(180deg, #FFFFFF 0%, #FAFAF8 100%)',
        borderTop: `1px solid ${T.border}`,
      }}
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={scene.id + '-caption'}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="flex items-center gap-3"
        >
          <span
            className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{
              background: scene.accent + '18',
              border: `1px solid ${scene.accent}33`,
            }}
          >
            <Icon className="w-4 h-4" style={{ color: scene.accent }} />
          </span>
          <div className="flex-1 min-w-0">
            <div
              className="text-[9.5px] uppercase tracking-[0.18em] font-semibold"
              style={{ color: scene.accent }}
            >
              {scene.kicker}
            </div>
            <div
              className="text-[13px] sm:text-[13.5px] font-medium leading-snug"
              style={{ color: T.ink }}
            >
              {scene.title}
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

/* ============================================================
   📊 PROGRESS BARS (carousel state)
   ============================================================ */
const ProgressBars = ({ activeIndex, count }) => (
  <div className="flex items-center gap-1.5">
    {Array.from({ length: count }).map((_, i) => (
      <span
        key={i}
        className="rounded-full"
        style={{
          width: i === activeIndex ? 22 : 6,
          height: 3,
          background: i === activeIndex ? T.gold : 'rgba(28,17,71,0.18)',
          boxShadow: i === activeIndex ? `0 0 6px ${T.gold}88` : 'none',
          transition: 'all 380ms ease',
        }}
      />
    ))}
  </div>
);

/* ============================================================
   🎯 MAIN COMPONENT
   ============================================================ */
export const HeroDemoVideo = () => {
  const navigate = useNavigate();
  const SCENES = useMemo(() => _buildPreviewScenes(), []);
  const [activeScene, setActiveScene] = useState(0);
  const [paused, setPaused] = useState(false);
  const [hasMp4, setHasMp4] = useState(null); // null=loading, true/false
  const videoRef = useRef(null);

  const openFullDemo = useCallback(() => {
    navigate('/demo', { state: { fromDemoTrigger: true } });
  }, [navigate]);

  // Detect optional asset (zero hidden fallback : we log explicitly)
  // Check Content-Type to avoid SPA fallback returning index.html for missing files.
  useEffect(() => {
    let cancelled = false;
    fetch('/assets/videos/coachdia-demo.mp4', { method: 'HEAD' })
      .then((r) => {
        if (cancelled) return;
        const ct = r.headers.get('content-type') || '';
        if (r.ok && ct.startsWith('video/')) {
          setHasMp4(true);
          // eslint-disable-next-line no-console
          console.log('[HeroDemoVideo] ✅ asset coachdia-demo.mp4 détecté (' + ct + ')');
        } else {
          setHasMp4(false);
          // eslint-disable-next-line no-console
          console.log('[HeroDemoVideo] ℹ️ asset .mp4 absent — rendu carrousel cinematic premium (aucun fallback caché).');
        }
      })
      .catch(() => {
        if (cancelled) return;
        setHasMp4(false);
        // eslint-disable-next-line no-console
        console.log('[HeroDemoVideo] ℹ️ asset .mp4 indisponible — rendu carrousel cinematic premium.');
      });
    return () => { cancelled = true; };
  }, []);

  // Auto-rotate scenes (preview)
  useEffect(() => {
    if (paused) return undefined;
    const t = setInterval(() => {
      setActiveScene((s) => (s + 1) % SCENES.length);
    }, SCENE_DURATION_MS);
    return () => clearInterval(t);
  }, [paused, SCENES.length]);

  const scene = SCENES[activeScene];

  const goNext = useCallback(
    () => setActiveScene((s) => (s + 1) % SCENES.length),
    [SCENES.length]
  );
  const goPrev = useCallback(
    () => setActiveScene((s) => (s - 1 + SCENES.length) % SCENES.length),
    [SCENES.length]
  );

  return (
    <>
      {/* ============== PREVIEW CARD ============== */}
      <div
        className="relative w-full"
        data-testid="hero-demo-video"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        {/* Outer glow */}
        <span
          aria-hidden
          className="absolute -inset-4 rounded-[28px] pointer-events-none"
          style={{
            background:
              'radial-gradient(60% 60% at 50% 50%, rgba(212,175,55,0.18) 0%, rgba(212,175,55,0) 70%)',
            filter: 'blur(24px)',
          }}
        />

        <div
          className="relative rounded-[24px] overflow-hidden"
          style={{
            background: '#FFFFFF',
            border: `1px solid rgba(212,175,55,0.25)`,
            boxShadow:
              '0 1px 0 rgba(255,255,255,0.6) inset, 0 30px 60px -28px rgba(28,17,71,0.30), 0 0 0 1px rgba(212,175,55,0.10)',
          }}
        >
          {/* Aspect ratio — mobile: pt-[82%] (plus haut pour messages complets)
              desktop: sm:pt-[66%] (ratio original premium) */}
          <div className="relative w-full pt-[82%] sm:pt-[66%]">
            {hasMp4 === true ? (
              <video
                ref={videoRef}
                className="absolute inset-0 w-full h-full object-cover"
                src="/assets/videos/coachdia-demo.mp4"
                poster="/assets/images/demo-poster.webp"
                muted
                playsInline
                autoPlay
                loop
                preload="metadata"
              />
            ) : (
              <AnimatePresence mode="wait">
                <motion.div
                  key={scene.id}
                  initial={{ opacity: 0, scale: 1.02 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.985 }}
                  transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                  className="absolute inset-0"
                >
                  <SceneFrame scene={scene} showCaption={false} />
                </motion.div>
              </AnimatePresence>
            )}
          </div>

          {/* Caption strip — bandeau dédié SOUS la zone visuelle (zéro chevauchement) */}
          {hasMp4 !== true && <CaptionStrip scene={scene} />}

          {/* Footer card : progress + open full screen */}
          <div className="px-5 py-4 flex items-center justify-between gap-3"
            style={{ background: '#FAFAF8', borderTop: `1px solid ${T.border}` }}
          >
            <div className="flex items-center gap-3 min-w-0">
              <ProgressBars activeIndex={activeScene} count={SCENES.length} />
              <span
                className="hidden sm:inline-flex text-[11px] font-medium tabular-nums"
                style={{ color: T.inkSoft }}
                data-testid="hero-demo-scene-indicator"
              >
                {String(activeScene + 1).padStart(2, '0')} / {String(SCENES.length).padStart(2, '0')}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={goPrev}
                aria-label="Scène précédente"
                className="w-9 h-9 rounded-full flex items-center justify-center transition-all hover:bg-[#FFFCEC]"
                style={{
                  background: '#FFFFFF',
                  border: `1px solid ${T.border}`,
                  color: T.ink,
                }}
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={goNext}
                aria-label="Scène suivante"
                className="w-9 h-9 rounded-full flex items-center justify-center transition-all hover:bg-[#FFFCEC]"
                style={{
                  background: '#FFFFFF',
                  border: `1px solid ${T.border}`,
                  color: T.ink,
                }}
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={openFullDemo}
                className="ml-1.5 inline-flex items-center gap-1.5 px-4 h-9 rounded-full text-[12px] font-semibold transition-all"
                style={{
                  background: `linear-gradient(135deg, ${T.goldDeep}, ${T.gold}, ${T.goldLight})`,
                  color: T.ink,
                  boxShadow: '0 6px 14px -6px rgba(212,175,55,0.55)',
                }}
                data-testid="hero-demo-fullscreen-btn"
              >
                <Play className="w-3.5 h-3.5" />
                Voir la démo
              </button>
            </div>
          </div>
        </div>

        {/* Tiny floating cards around (depth) */}
        <motion.div
          aria-hidden
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="hidden md:flex absolute -bottom-5 -left-4 items-center gap-2 px-3 py-2 rounded-2xl"
          style={{
            background: '#FFFFFF',
            border: `1px solid ${T.border}`,
            boxShadow: '0 14px 30px -16px rgba(28,17,71,0.25)',
          }}
        >
          <span
            className="w-7 h-7 rounded-lg flex items-center justify-center"
            style={{
              background: 'linear-gradient(135deg, #F4EFFF, #FFFFFF)',
              border: `1px solid ${T.border}`,
            }}
          >
            <Brain className="w-3.5 h-3.5" style={{ color: T.violet }} />
          </span>
          <div className="leading-tight">
            <div className="text-[10px] font-semibold uppercase tracking-[0.16em]" style={{ color: T.violet }}>
              Owen IA
            </div>
            <div className="text-[11px] font-medium" style={{ color: T.ink }}>
              Temps réel
            </div>
          </div>
        </motion.div>
        <motion.div
          aria-hidden
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55 }}
          className="hidden md:flex absolute -top-3 -right-3 items-center gap-2 px-3 py-2 rounded-2xl"
          style={{
            background: '#FFFFFF',
            border: `1px solid ${T.border}`,
            boxShadow: '0 14px 30px -16px rgba(28,17,71,0.25)',
          }}
        >
          <CheckCircle2 className="w-4 h-4" style={{ color: T.green }} />
          <span className="text-[11px] font-semibold" style={{ color: T.ink }}>
            Validé sur preuves
          </span>
        </motion.div>
      </div>
    </>
  );
};

export default HeroDemoVideo;
